import React from "react";

import {
  ReviewCard,
  ReviewCardTitle,
  ReviewField,
  ReviewFieldGrid,
} from "components/ReviewCard";
import { Text } from "components/Text";

import { eContractFieldInfoText } from "constants/eContract";

import { formatAddress } from "lib/formatAddress";
import { formatDate } from "lib/timeFormats";

import {
  computeLotDisplayValues,
  useEContractReviewData,
} from "hooks/useEContractReviewData";

export const EContractGenericReviewSection = ({
  agreementId,
}: {
  agreementId: string;
}): React.JSX.Element => {
  const {
    vendor,
    vendorRegistration,
    buyer,
    lots,
    sale,
    consignments,
    sexes,
    ageOptions,
    breedOptions,
    originatingPICs,
    destinationPICs,
    nvds,
    singleDeliveryDate,
    commissionDisplay,
    commissionSourceDisplay,
    interestDisplay,
    agentAddress,
  } = useEContractReviewData(agreementId);

  return (
    <>
      {/* Vendor */}
      <ReviewCard>
        <ReviewCardTitle>Vendor</ReviewCardTitle>
        <ReviewFieldGrid>
          <ReviewField
            xs={7}
            md={10}
            label="Vendor Name"
            infoText={eContractFieldInfoText.vendorName}
          >
            {vendor?.name ?? "-"}
          </ReviewField>
          <ReviewField
            xs={5}
            md={2}
            label="PIC No"
            infoText={eContractFieldInfoText.vendorPicNo}
          >
            {originatingPICs.length > 0 ? originatingPICs.join(", ") : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="Address"
            infoText={eContractFieldInfoText.vendorAddress}
          >
            {vendor ? formatAddress(vendor.address) ?? "-" : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="NVD No"
            infoText={eContractFieldInfoText.nvdNo}
          >
            {nvds.length > 0 ? nvds.join(", ") : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={6}
            label="ABN"
            infoText={eContractFieldInfoText.vendorAbn}
          >
            {vendorRegistration?.abn || "-"}
          </ReviewField>
          <ReviewField
            xs={6}
            label="GST Registered"
            infoText={eContractFieldInfoText.vendorGst}
          >
            {vendor
              ? vendorRegistration?.isGSTRegistered
                ? "Yes"
                : "No"
              : "-"}
          </ReviewField>
        </ReviewFieldGrid>
      </ReviewCard>

      {/* Purchaser */}
      <ReviewCard>
        <ReviewCardTitle>Purchaser</ReviewCardTitle>
        <ReviewFieldGrid>
          <ReviewField
            xs={7}
            md={10}
            label="Purchaser Name"
            infoText={eContractFieldInfoText.purchaserName}
          >
            {buyer?.name ?? "-"}
          </ReviewField>
          <ReviewField
            xs={5}
            md={2}
            label="PIC No"
            infoText={eContractFieldInfoText.purchaserPicNo}
          >
            {destinationPICs.length > 0 ? destinationPICs.join(", ") : "-"}
          </ReviewField>
        </ReviewFieldGrid>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="Address"
            infoText={eContractFieldInfoText.purchaserAddress}
          >
            {buyer ? formatAddress(buyer.address) ?? "-" : "-"}
          </ReviewField>
        </ReviewFieldGrid>
      </ReviewCard>

      {/* Agent */}
      <ReviewCard>
        <ReviewCardTitle>Agent</ReviewCardTitle>
        <ReviewFieldGrid>
          <ReviewField
            xs={12}
            label="Address"
            infoText={eContractFieldInfoText.agentAddress}
          >
            {formatAddress(agentAddress) ?? "-"}
          </ReviewField>
        </ReviewFieldGrid>
      </ReviewCard>

      {/* Sale Details */}
      <ReviewCard>
        <ReviewCardTitle>Sale</ReviewCardTitle>
        <ReviewFieldGrid>
          <ReviewField
            xs={6}
            label="Sale Date"
            infoText={eContractFieldInfoText.saleDate}
          >
            {sale?.date ? formatDate(sale.date) : "-"}
          </ReviewField>
          <ReviewField
            xs={6}
            label="Delivery Date"
            infoText={eContractFieldInfoText.deliveryDate}
          >
            {singleDeliveryDate ? formatDate(singleDeliveryDate) : "-"}
          </ReviewField>
          <ReviewField
            xs={6}
            label="Default Interest Rate"
            infoText={eContractFieldInfoText.interestRate}
          >
            {interestDisplay ?? "-"}
          </ReviewField>
          <ReviewField
            xs={6}
            label="Commission"
            infoText={
              commissionSourceDisplay
                ? `Resolved via the ${commissionSourceDisplay}`
                : eContractFieldInfoText.commission
            }
          >
            {commissionDisplay ?? "-"}
          </ReviewField>
        </ReviewFieldGrid>
      </ReviewCard>

      {/* Livestock Lots */}
      {lots.length === 0 ? (
        <ReviewCard>
          <ReviewCardTitle>Livestock</ReviewCardTitle>
          <Text italic color="titleMedium">
            No lots found for this agreement.
          </Text>
        </ReviewCard>
      ) : (
        lots.map(lot => {
          const { description, unitPrice, weightKg } = computeLotDisplayValues(
            lot,
            sexes,
            ageOptions,
            breedOptions,
          );
          const consignment = consignments[lot.consignment_id];
          const brands = consignment?.brands || null;

          return (
            <ReviewCard key={lot.id}>
              <ReviewCardTitle>Lot {lot.lot_number}</ReviewCardTitle>

              <ReviewFieldGrid>
                <ReviewField
                  xs={6}
                  label="Qty"
                  infoText={eContractFieldInfoText.lotQty}
                >
                  {lot.quantity || "-"}
                </ReviewField>
                <ReviewField
                  xs={6}
                  label="Description"
                  infoText={eContractFieldInfoText.lotDescription}
                >
                  {description}
                </ReviewField>
              </ReviewFieldGrid>

              <ReviewFieldGrid>
                <ReviewField
                  xs={6}
                  label="Price"
                  infoText={eContractFieldInfoText.lotPrice}
                >
                  {unitPrice ?? "-"}
                </ReviewField>
                <ReviewField
                  xs={6}
                  label="Weight"
                  infoText={eContractFieldInfoText.lotWeight}
                >
                  {weightKg ?? "-"}
                </ReviewField>
                <ReviewField
                  xs={6}
                  label="Hd Delivered"
                  infoText={eContractFieldInfoText.lotHdDelivered}
                >
                  {lot.quantity_delivered ?? "-"}
                </ReviewField>
                <ReviewField
                  xs={6}
                  label="Brands"
                  infoText={eContractFieldInfoText.brands}
                >
                  {brands ?? "-"}
                </ReviewField>
              </ReviewFieldGrid>

              <ReviewFieldGrid>
                <ReviewField
                  xs={12}
                  label="Lot Notes"
                  infoText={eContractFieldInfoText.lotNotes}
                >
                  {lot.note || "-"}
                </ReviewField>
              </ReviewFieldGrid>
            </ReviewCard>
          );
        })
      )}
    </>
  );
};
